import { isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  PLATFORM_ID,
  inject,
  input,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  radixChevronUp,
  radixGlobe,
  radixHamburgerMenu,
  radixMoon,
  radixSun,
} from '@ng-icons/radix-icons';

import {
  DeviceDetectionService,
  trackDownloadClick,
} from '@ledgerly/common/browser';
import { ThemeService } from '@ledgerly/shared/theming';

import { LogoComponent } from '../logo/logo.component';
import { NAV_LINKS, SITE_LOCALES, SiteLocale } from '../nav-link';

@Component({
  selector: 'layout-header',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [NgIconComponent, LogoComponent, RouterLink],
  templateUrl: './header.component.html',
  viewProviders: [
    provideIcons({
      radixHamburgerMenu,
      radixChevronUp,
      radixGlobe,
      radixSun,
      radixMoon,
    }),
  ],
})
export class HeaderComponent {
  /**
   * True where the header sits on the indigo band: the landing page, whose
   * hero is the band. False on `/terms` and `/privacy`, which have no band.
   */
  readonly onBand = input(false);

  protected readonly navLinks = NAV_LINKS;

  /**
   * Every locale is a real URL, so the picker is a set of links a crawler can
   * follow and a reader can bookmark. Nothing is remembered and nothing is
   * negotiated: English is the root, the rest are sub-paths.
   *
   * Clicking one used to also set a `firebase-language-override` cookie, which
   * existed only to steer Firebase Hosting's language negotiation. That is off
   * now (see firebase.json and CLAUDE.md), so the cookie did nothing but exist.
   */
  protected readonly locales = SITE_LOCALES;

  protected theme = 'dark';
  protected showMenu = false;

  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly themeService = inject(ThemeService);
  private readonly deviceDetection = inject(DeviceDetectionService);

  constructor() {
    this.themeService.getTheme().subscribe({
      next: (theme: string) => (this.theme = theme),
    });
  }

  /**
   * Which locale this build is. `<html lang>` is set by the build itself, so
   * it is the one source that cannot drift from the bundle the reader is
   * actually running.
   */
  protected get currentLocale(): SiteLocale {
    if (isPlatformServer(this.platformId)) return this.locales[0];

    const lang = this.document.documentElement.lang;
    return this.locales.find((l) => l.code === lang) ?? this.locales[0];
  }

  /** The root of the current locale, so the logo goes home, not to English. */
  protected get homeHref(): string {
    return this.currentLocale.path;
  }

  protected get appStoreLink(): string {
    return this.deviceDetection.getAppStoreLink();
  }

  protected get themeToggleLabel(): string {
    return this.theme === 'light'
      ? $localize`:@@themeToDark:Switch to the dark theme`
      : $localize`:@@themeToLight:Switch to the light theme`;
  }

  protected get languageLabel(): string {
    return $localize`:@@languageMenu:Choose a language`;
  }

  protected get menuLabel(): string {
    return $localize`:@@menuToggle:Menu`;
  }

  protected toggleTheme(): void {
    this.theme = this.theme === 'light' ? 'dark' : 'light';
    this.themeService.changeTheme(this.theme as 'light' | 'dark', false);
  }

  protected trackDownloadClick(location: string): void {
    trackDownloadClick(location);
  }
}
